import {
  faBed,
  faCheck,
  faTrash,
  faPenToSquare,
  faCircleInfo,
  faRobot,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, Fragment, SyntheticEvent, useContext, useState } from 'react';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import Tabs from 'components/ui/Tabs/Tabs';
import Rating from 'components/ui/Rating/Rating';
import { useTranslation } from 'react-i18next';
import { DynamicProposal, Proposal } from 'ts/shared/interfaces/CreateProposalParams';
import TripadvisorData from '../TripadvisorGoogle/TripadvisorData';
import {
  hotelsTripadvisor,
  loadGoogleByDynamicProposal,
  loadGoogleByProposal,
} from 'services/proposal';
import ButtonsLang from '../TripadvisorGoogle/ButtonsLang';
import useRecaptcha from 'hooks/useRecaptcha';
import { showWarning } from 'utils/utils';
import GoogleHotelReviews from '../TripadvisorGoogle/GoogleHotelReviews';
import { saveProposal } from 'utils/shared/idbUrl';
import withErrorBoundary from '../../withErrorBoundary';
import { dateFormat } from 'utils/shared/utils';
import FreeTextModal from '../Single/FreeTextModal';
import ProposalNewRooms from '../ProposalNewRooms';
import LoadingBlock from '../../LoadingBlock';
import { ClaudeHotelInfoConfig } from 'utils/shared/claudeProposalInformation';
import { HotelInfoConfig, resolveHotelInfo } from 'utils/shared/hotelProposalInformation';
import ProposalHotelInformation from './ProposalHotelInformation';
import ProposalClaudeHotelInformation from './ProposalClaudeHotelInformation';
import { resolveProposalLanguageFromBranch } from 'utils/shared/proposal';
import { MainContext } from 'contexts/MainContext';
import { imgBg, gIco, trIco } from 'constants/generic';
import { useProposalUi } from '../Assistant/proposalActions';

interface HotelProposal2Props {
  hotel: any;
  proposal: Proposal;
  onChangeProposal: (proposal: Proposal) => void;
  hotelIndex: number;
  dynamic?: DynamicProposal;
  openPopup: (product: string, index: number, rcUniqueKey?: string) => void;
  defaultShowCancellationDate?: boolean;
  defaultCancellationDateDelta?: number;
  assistantOwner?: boolean;
  tripBuilderMode?: boolean;
}

const HotelProposal2: FC<HotelProposal2Props> = ({
  proposal,
  hotel,
  onChangeProposal,
  hotelIndex,
  dynamic,
  openPopup,
  defaultShowCancellationDate = true,
  defaultCancellationDateDelta = 0,
  assistantOwner = false,
  tripBuilderMode = false,
}) => {
  const {
    user
  } = useContext(MainContext);
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [showAllRooms, setShowAllRooms] = useState(false);
  const [loadingTripadvisor, setLoadingTripadvisor] = useState('');
  const [loadingGoogle, setLoadingGoogle] = useState('');
  const defaultLang = resolveProposalLanguageFromBranch(user?.branch?.ext_1_pcc);
  const [claudeLang, setClaudeLang] = useState(
    () => (!dynamic ? proposal.hotels[hotelIndex]?.claudeHotelInfoConfig?.language : undefined),
  );
  const { handleRecapcha } = useRecaptcha();
  const hotelProposal = dynamic ? proposal.dynamic[hotelIndex] : proposal.hotels[hotelIndex];
  const googleLang = hotelProposal.googleChecked?.language ?? defaultLang;
  const tripAdvisorLang = hotelProposal.tripAdvisorChecked?.language ?? defaultLang;
  const [showFreeModal, setShowFreeModal] = useState(false);

  const onImageLoadError = ({ currentTarget }: SyntheticEvent<HTMLImageElement, Event>) => {
    currentTarget.onerror = null;
    currentTarget.src = imgBg;
  };

  const onClickLoadTripadvisor = async (lang: string) => {
    setLoadingTripadvisor(lang);

    if (dynamic) {
      const newRes = await hotelsTripadvisor({
        bpid: dynamic.hotel?.item?.bpid || '',
        lang: lang,
      });

      const updatedProposal = {
        ...proposal,
        dynamic: proposal.dynamic.map((d) =>
          d.searchToken === dynamic.searchToken
            ? {
              ...d,
              tripAdvisorData: newRes,
              tripAdvisorChecked: {
                details: d.tripAdvisorChecked?.details ?? false,
                reviews: d.tripAdvisorChecked?.reviews ?? false,
                amenities: d.tripAdvisorChecked?.amenities ?? false,
                language: lang,
              },
            }
            : d
        ),
      };
      onChangeProposal(updatedProposal);
    } else {
      const newRes = await hotelsTripadvisor({
        bpid: hotel.item.bpid,
        lang: lang,
      });

      const updatedProposal = {
        ...proposal,
        hotels: proposal.hotels.map((h, index) =>
          index === hotelIndex
            ? {
              ...h,
              tripAdvisorData: newRes,
              tripAdvisorChecked: {
                details: h.tripAdvisorChecked?.details ?? false,
                reviews: h.tripAdvisorChecked?.reviews ?? false,
                amenities: h.tripAdvisorChecked?.amenities ?? false,
                language: lang,
              },
            }
            : h
        ),
      };
      onChangeProposal(updatedProposal);
    }

    setLoadingTripadvisor('');
  };

  const onClickLoadGoogle = async (lang: string) => {
    if (dynamic) {
      setLoadingGoogle(lang);
      const token = await handleRecapcha();
      if (!token) {
        setLoadingGoogle('');
        return;
      }
      const newRes = await loadGoogleByDynamicProposal(dynamic, lang, token);
      const serpHotel = newRes?.serpResults?.serpHotel;
      onChangeProposal({
        ...proposal,
        dynamic: proposal.dynamic.map((d) =>
          d.searchToken === dynamic.searchToken
            ? {
              ...d,
              googleData: newRes,
              googleChecked: {
                imagesIndexes: d.googleChecked?.imagesIndexes ?? [],
                reviews: d.googleChecked?.reviews ?? !!serpHotel?.reviews,
                language: lang,
              },
            }
            : d
        ),
      });
      setLoadingGoogle('');
    } else {
      if (!proposal.hotels[hotelIndex].checkIn || !proposal.hotels[hotelIndex].checkOut) {
        showWarning("Sorry we can't load now, please try again later");
        return;
      }

      setLoadingGoogle(lang);

      const token = await handleRecapcha();
      if (!token) {
        setLoadingGoogle('');
        return;
      }

      const newRes = await loadGoogleByProposal(proposal.hotels[hotelIndex], lang, token);

      const serpHotel = newRes?.serpResults?.serpHotel;
      const currentHotel = proposal.hotels[hotelIndex];
      const updatedProposal = {
        ...proposal,
        hotels: proposal.hotels.map((h, index) =>
          index === hotelIndex
            ? {
              ...h,
              googleData: newRes,
              googleChecked: {
                imagesIndexes: currentHotel.googleChecked?.imagesIndexes ?? [],
                reviews: currentHotel.googleChecked?.reviews ?? !!serpHotel?.reviews,
                language: lang,
              },
            }
            : h
        ),
      };
      onChangeProposal(updatedProposal);
    }
    setLoadingGoogle('');
  };

  const onClickRemove = () => {
    const newProposals = proposal.hotels.filter((_, prIndex) => hotelIndex !== prIndex);
    onChangeProposal({ ...proposal, hotels: newProposals });
  };

  const onChangeRoom = (value: string) => {
    if (dynamic) {
      const newDynamic = proposal.dynamic.map((d) => {
        if (d.searchToken === dynamic.searchToken) {
          const newChoosenRoomIds = d.hotel?.choosenRoomIds?.includes(value)
            ? d.hotel.choosenRoomIds?.filter((id: string) => id !== value)
            : [...(d.hotel?.choosenRoomIds || []), value];

          return { ...d, hotel: { ...d.hotel, choosenRoomIds: newChoosenRoomIds } };
        }
        return d;
      }) as DynamicProposal[];

      onChangeProposal({
        ...proposal,
        dynamic: newDynamic,
      });
    } else {
      const newProposal = {
        ...proposal,
        hotels: proposal.hotels.map((h, index) => {
          const choosenRoomIds =
            index === hotelIndex
              ? h.item.choosenRoomIds?.includes(value)
                ? h.item.choosenRoomIds?.filter((id: string) => id !== value)
                : [...(h.item.choosenRoomIds || []), value]
              : h.item.choosenRoomIds;

          return {
            ...h,
            item: {
              ...h.item,
              choosenRoomIds,
            },
          };
        }),
      };

      onChangeProposal(newProposal);
    }
  };

  const onChangePrice = (hotel: any) => {
    onChangeProposal({
      ...proposal,
      hotels: proposal.hotels.map((h, index) => {
        return index === hotelIndex ? { ...h, item: hotel } : h;
      }),
    });
  };

  const onChangeChecked = async (
    type: 'googleChecked' | 'tripAdvisorChecked',
    field: string,
    value: any
  ) => {
    let updatedProposal: any;
    if (dynamic) {
      updatedProposal = {
        ...proposal,
        dynamic: proposal.dynamic.map((d) => {
          if (d.searchToken === dynamic.searchToken) {
            return { ...d, [type]: { ...(d as any)[type], [field]: value } };
          }
          return d;
        }),
      };
    } else {
      updatedProposal = {
        ...proposal,
        hotels: proposal.hotels.map((h, index) =>
          index === hotelIndex
            ? {
              ...h,
              [type]: {
                ...(h as any)[type],
                [field]: value,
              },
            }
            : h
        ),
      };
    }
    onChangeProposal(updatedProposal);
    await saveProposal('proposal', updatedProposal);
  };

  const onChangeClaudeConfig = async (claudeHotelInfoConfig: ClaudeHotelInfoConfig) => {
    const updatedProposal = {
      ...proposal,
      hotels: proposal.hotels.map((h, index) =>
        index === hotelIndex ? { ...h, claudeHotelInfoConfig } : h
      ),
    };
    onChangeProposal(updatedProposal);
    await saveProposal('proposal', updatedProposal);
  };

  const onChangeHotelInfoConfig = async (hotelInfoConfig: HotelInfoConfig) => {
    const updatedProposal = {
      ...proposal,
      hotels: proposal.hotels.map((h, index) =>
        index === hotelIndex ? { ...h, hotelInfoConfig } : h
      ),
    };
    onChangeProposal(updatedProposal);
    await saveProposal('proposal', updatedProposal);
  };

  const onSaveFreeText = (
    desc: string,
    displayOrder?: number,
    cancellationSettings?: { showCancellationDate: boolean; cancellationDateDelta: number },
  ) => {
    if (dynamic) {
      const updatedProposal = {
        ...proposal,
        dynamic: proposal.dynamic.map((d, index) =>
          index === hotelIndex ? { ...d, freeTextHotel: desc, displayOrder } : d
        ),
      };
      onChangeProposal(updatedProposal);
      return;
    }

    const updatedProposal = {
      ...proposal,
      hotels: proposal.hotels.map((h, index) =>
        index === hotelIndex
          ? {
            ...h,
            freeText: desc,
            displayOrder,
            showCancellationDate: cancellationSettings?.showCancellationDate,
            cancellationDateDelta: cancellationSettings?.cancellationDateDelta,
          }
          : h
      ),
    };
    onChangeProposal(updatedProposal);
  };

  useProposalUi(
    (intent) => {
      if (!assistantOwner) {
        return false;
      }
      if (intent.kind === 'hotel-tab' || intent.kind === 'show-rooms' || intent.kind === 'load-source') {
        return intent.hotelIndex === hotelIndex && intent.dynamic === !!dynamic;
      }
      if (intent.kind === 'open-edit') {
        return (
          intent.index === hotelIndex &&
          intent.dynamic === !!dynamic &&
          (intent.product === 'hotels' || intent.product === 'dynamic') &&
          (!intent.part || intent.part === 'hotel')
        );
      }
      return intent.kind === 'claude-lang' && !dynamic && intent.hotelIndex === hotelIndex;
    },
    (intent) => {
      if (intent.kind === 'hotel-tab') {
        setActiveTab(intent.tab);
      }
      if (intent.kind === 'show-rooms') {
        setShowAllRooms(intent.show);
      }
      if (intent.kind === 'load-source') {
        if (intent.source === 'google') {
          void onClickLoadGoogle(intent.language);
        } else {
          void onClickLoadTripadvisor(intent.language);
        }
      }
      if (intent.kind === 'claude-lang') {
        setClaudeLang(intent.language);
      }
      if (intent.kind === 'open-edit') {
        setShowFreeModal(true);
      }
    },
  );

  return (
    <div className={'pr-hotel default-shadow mb-20' + (tripBuilderMode ? ' pr-hotel--tb' : '')}>
      <div className="pr-hotel-head title-header-grey text-bold flex-between">
        <span>{hotel?.item.name}</span>
        <div>
          <FontAwesomeIcon onClick={() => setShowFreeModal(true)} icon={faPenToSquare} />
          {!dynamic && <FontAwesomeIcon onClick={onClickRemove} icon={faTrash} />}
        </div>
      </div>
      <div className="pr-hotel-body">
        <div className="pr-hotel-side">
          <div className="mb-15">
            <div className="mb-5 mt-5">
              <Rating className="stars" value={hotel.item.star} color="var(--color-contrast)" />
            </div>
            <div className="mb-5 pr-hotel-address">
              {hotel.addressInfo.address}
            </div>
            <div className="mb-5">
              {dateFormat(hotel.roomClasses[0].checkIn, 'dd MMM yyyy', 'en')} -{' '}
              {dateFormat(hotel.roomClasses[0].checkOut, 'dd MMM yyyy', 'en')}
            </div>
          </div>
          <div className="slider-main-image">
            <img src={hotel?.info?.picture || imgBg} alt={'1'} onError={onImageLoadError} />
          </div>
        </div>
        {!tripBuilderMode && (
        <div className="pr-hotel-main">
          <div className="flex-column h100p">
            <Tabs
              label={t('hotel.hotel')}
              value={String(activeTab)}
              onValueChange={(value) => setActiveTab(Number(value))}
              className="text-sm"
              items={[
                {
                  value: '0',
                  label: (
                    <span className="proposal-tab-label">
                      Rooms
                      <FontAwesomeIcon icon={faBed} />
                    </span>
                  ),
                },
                {
                  value: '1',
                  label: (
                    <span className="proposal-tab-label">
                      {t('hotel.info')}
                      <FontAwesomeIcon icon={faCircleInfo} />
                    </span>
                  ),
                },
                {
                  value: '3',
                  label: (
                    <span className="proposal-tab-label">
                      WWW
                      <img src={gIco} alt="G" />
                    </span>
                  ),
                },
                {
                  value: '2',
                  label: (
                    <span className="proposal-tab-label">
                      TripAdvisor
                      <img src={trIco} alt="T" />
                    </span>
                  ),
                },
                {
                  value: '4',
                  label: (
                    <span className="proposal-tab-label">
                      AI Hotel Info
                      <FontAwesomeIcon icon={faRobot} />
                    </span>
                  ),
                },
              ]}
            />
            <div className="wrapper w100p h100p mt-10">
              {activeTab === 0 && (
                <div className="h100p">
                  <ProposalNewRooms
                    rooms={
                      showAllRooms
                        ? hotel.roomClasses
                        : hotel.roomClasses.filter((rc: any) =>
                          hotel.choosenRoomIds?.includes(rc.rcUniqueKey)
                        )
                    }
                    hotel={hotel}
                    choosenRoomIds={hotel.choosenRoomIds || []}
                    setChoosenRoomIds={onChangeRoom}
                    onChangePrice={onChangePrice}
                    dynamic={dynamic}
                    openPopup={openPopup}
                    hotProposal={proposal.hotels[hotelIndex]}
                    hotelIndex={hotelIndex}
                    assistantOwner={assistantOwner}
                    showAllRooms={showAllRooms}
                    onShowAllRoomsChange={setShowAllRooms}
                    totalRoomsCount={hotel.roomClasses.length}
                  />
                </div>
              )}
              {activeTab === 1 && (
                <ProposalHotelInformation
                  hotelData={
                    !dynamic
                      ? resolveHotelInfo(proposal.hotels[hotelIndex]?.hotelInformation)
                      : undefined
                  }
                  config={!dynamic ? proposal.hotels[hotelIndex]?.hotelInfoConfig : undefined}
                  onChangeConfig={!dynamic ? onChangeHotelInfoConfig : undefined}
                />
              )}
              {activeTab === 4 && (
                <ProposalClaudeHotelInformation
                  claudeData={!dynamic ? proposal.hotels[hotelIndex]?.claudeData : undefined}
                  config={!dynamic ? proposal.hotels[hotelIndex]?.claudeHotelInfoConfig : undefined}
                  onChangeConfig={!dynamic ? onChangeClaudeConfig : undefined}
                  activeLanguage={claudeLang}
                  onActiveLanguageChange={setClaudeLang}
                />
              )}

              {activeTab === 3 && (
                <div className="tripadvisor-data">
                  <ButtonsLang onClickLoad={onClickLoadGoogle} loading={loadingGoogle} activeLanguage={googleLang} />

                  {!!hotelProposal.googleData &&
                    !hotelProposal.googleData?.serpResults?.serpHotel && (
                      <div className="mt-20 text-bold">Hotel was not found</div>
                    )}
                  {!hotelProposal.googleData && (<div className="mt-20 text-bold">
                    <LoadingBlock />
                  </div>)}
                  {!!hotelProposal.googleData?.serpResults?.serpHotel && (
                    <div>
                      <div className="text-sm">
                        <div className="mb-10 mt-10">{t('proposal.add_to')}</div>

                        <Checkbox
                          className="mb-5"
                          checked={hotelProposal.googleChecked?.reviews}
                          onChange={() => {
                            onChangeChecked(
                              'googleChecked',
                              'reviews',
                              !hotelProposal.googleChecked?.reviews || false
                            );
                          }}
                          label={t('proposal.reviews')}
                        />
                      </div>
                      <GoogleHotelReviews res={hotelProposal.googleData} />
                      {hotelProposal.googleData?.serpResults?.serpHotel?.description && <div className='title mt-20'>{t('hotel.description')}</div>}
                      <div className='text-lh text-sm mb-20 mt-10'>{hotelProposal.googleData?.serpResults?.serpHotel?.description}</div>
                      <div className="pr-photos-wr">
                        {hotelProposal.googleData?.serpResults?.serpHotel?.images?.map(
                          (im: any, index: number) => (
                            <div key={index} className="h-photos position-relative">
                              <img
                                src={im.originalImage}
                                alt="1"
                                onError={(e) =>
                                  ((e.target as any).parentNode.style.display = 'none')
                                }
                                style={{ pointerEvents: 'none' }}
                              />
                              <div className="pr-photos-checkbox">
                                <div
                                  onClick={() => {
                                    const currentIndexes =
                                      proposal.hotels[hotelIndex].googleChecked?.imagesIndexes ||
                                      [];
                                    const newIndexes = currentIndexes.includes(index)
                                      ? currentIndexes.filter((i) => i !== index)
                                      : [...currentIndexes, index];
                                    onChangeChecked('googleChecked', 'imagesIndexes', newIndexes);
                                  }}
                                  className={`image-checkbox ${hotelProposal.googleChecked?.imagesIndexes?.includes(index)
                                    ? 'selected'
                                    : ''
                                    }`}
                                >
                                  {hotelProposal.googleChecked?.imagesIndexes?.includes(index) && (
                                    <FontAwesomeIcon icon={faCheck} size="xs" />
                                  )}
                                </div>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {activeTab === 2 && (
                <div className="tripadvisor-data">
                  <ButtonsLang onClickLoad={onClickLoadTripadvisor} loading={loadingTripadvisor} activeLanguage={tripAdvisorLang} />

                  {!!hotelProposal.tripAdvisorData?.tripAdvisorHotel?.amenities && (
                    <div className="text-sm">
                      {!!hotelProposal.tripAdvisorData && (
                        <Fragment>
                          <div className="mb-10 mt-10">{t('proposal.add_to')}</div>

                          <Checkbox
                            className="mb-5"
                            checked={hotelProposal.tripAdvisorChecked?.details}
                            onChange={() => {
                              onChangeChecked(
                                'tripAdvisorChecked',
                                'details',
                                !hotelProposal.tripAdvisorChecked?.details || false
                              );
                            }}
                            label={t('proposal.details')}
                          />
                          <Checkbox
                            className="mb-5"
                            checked={hotelProposal.tripAdvisorChecked?.reviews}
                            onChange={() => {
                              onChangeChecked(
                                'tripAdvisorChecked',
                                'reviews',
                                !hotelProposal.tripAdvisorChecked?.reviews || false
                              );
                            }}
                            label={t('proposal.reviews')}
                          />
                          <Checkbox
                            className="mb-5"
                            checked={hotelProposal.tripAdvisorChecked?.amenities}
                            onChange={() => {
                              onChangeChecked(
                                'tripAdvisorChecked',
                                'amenities',
                                !hotelProposal.tripAdvisorChecked?.amenities || false
                              );
                            }}
                            label={t('proposal.amenities')}
                          />
                        </Fragment>
                      )}
                    </div>
                  )}

                  <TripadvisorData data={hotelProposal.tripAdvisorData} showLoading={true} />
                </div>
              )}
            </div>
          </div>
        </div>
        )}
      </div>
      {showFreeModal && (
        <FreeTextModal
          close={() => setShowFreeModal(false)}
          onSave={onSaveFreeText}
          freeText={
            dynamic
              ? proposal.dynamic[hotelIndex]?.freeTextHotel || ''
              : proposal.hotels[hotelIndex]?.freeText || ''
          }
          displayOrder={hotelProposal.displayOrder}
          isHotel={!dynamic}
          showCancellationDate={
            !dynamic
              ? proposal.hotels[hotelIndex]?.showCancellationDate ?? defaultShowCancellationDate
              : undefined
          }
          cancellationDateDelta={
            !dynamic
              ? proposal.hotels[hotelIndex]?.cancellationDateDelta ?? defaultCancellationDateDelta
              : undefined
          }
        />
      )}
    </div>
  );
};

export default withErrorBoundary(HotelProposal2, 'HotelProposal2');
